import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useApiListOrgSsoDomain, useApiFormOrgSsoDomain } from '../../../../../api-new/state/auth'
import { normalizeDomain } from '../../../../../shared/form-new/form-tags'
import { domainTone, overrideDetail } from '../../../../../shared/component/status-pill'
import { DomainPill } from '../../../../../shared/sso-workspace/pill'
import { TXT_KEEP_HINT, TxtRecordFields } from '../../../../../shared/sso-workspace/txt-record'
import { useDomainCheck, DomainCheckLine } from '../../../../../shared/sso-workspace/domain-check'
import { ConfirmDialog } from '../../../../../shared/sso-workspace/confirm-dialog'
import { UnbindDialog } from '../../../../../shared/sso-workspace/unbind-dialog'
import { RequireSSOSwitch } from '../../../../../shared/sso-workspace/require-sso'
import { useLoad } from '../../../../../shared/hooks/load'
import { ErrorReference } from '../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../shared/error/use-error-line'

const serverMessage = (err, fallback) => (err && err.data && err.data.message) || fallback

const formatDate = value =>
  new Date(value).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })

const connectionName = connection => connection.displayName || connection.providerName

// The organization's own domains (Task 13), in the connection workspace's
// language (review I5): a domain is claimed once by the organization and
// moves through pending -> verified -> at_risk -> deactivated on its own
// DNS-driven lifecycle, or is held routable by an EducationXR override.
// "Add a domain" claims one; each row shows its state, the TXT record to
// publish until DNS proves it, Check now, Require SSO, the connections it
// sends people to (bind and unbind for connections the organization owns;
// read-only, with who to ask, for the ones EducationXR manages -- audit B6),
// Notify members once deactivated, and Remove.
//
// The domains are rows in the Domains section, split by dividers (.sw-rows);
// the TXT record a row still needs is a panel inside it (.sw-panel).
export const OrgSsoDomain = ({ connections, enabled, onConnectionsChanged }) => {
  const apiListOrgSsoDomain = useApiListOrgSsoDomain()
  const orgSsoDomain = useApiFormOrgSsoDomain()

  const [value, setValue] = useState('')
  const [hint, setHint] = useState(null)
  const [error, failure, setError] = useErrorLine()
  const [busy, setBusy] = useState(false)

  useLoad(() => apiListOrgSsoDomain.get(), [])

  const handleRefresh = async () => await apiListOrgSsoDomain.get()
  // A binding changed: the connections' domains, status (Active / Not in
  // use) and boundDomainCount (the last-domain warning) come from their own
  // list, so reload it too.
  const handleBindingsChanged = async () => {
    await handleRefresh()
    if (onConnectionsChanged) await onConnectionsChanged()
  }

  const handleAdd = async e => {
    e.preventDefault()
    setError(null)

    const domain = normalizeDomain(value)
    if (!domain) {
      setHint(value.trim() ? `"${value.trim()}" isn't a domain.` : 'Enter the email domain, for example example.org.')
      return
    }

    setHint(null)
    setBusy(true)
    try {
      await orgSsoDomain.postArg('', { domain })
      setValue('')
      await handleRefresh()
    } catch (err) {
      setError(err && err.status === 409
        ? 'This domain is already claimed by another organization.'
        : serverMessage(err, `${domain} couldn't be added.`), err)
    } finally {
      setBusy(false)
    }
  }

  const domains = apiListOrgSsoDomain.items

  return (
    <div className="d-grid gap-3">
      <form className="d-flex flex-wrap align-items-start gap-2" noValidate onSubmit={handleAdd}>
        <div className="sw-field flex-grow-1" style={{ maxWidth: 360 }}>
          <label className="sw-label" htmlFor="org-sso-add-domain">Add a domain</label>
          <input id="org-sso-add-domain" className="form-control" value={value} placeholder="example.org" autoComplete="off" spellCheck={false}
            disabled={!enabled} aria-describedby="org-sso-add-domain-hint" onChange={e => setValue(e.target.value)} />
          <div id="org-sso-add-domain-hint" className={hint ? 'sw-required' : 'sw-hint'}>{hint || 'The part after the @ in your staff and students’ email addresses.'}</div>
        </div>
        <button type="submit" className="btn sw-btn sw-btn-primary" style={{ marginTop: '1.6rem' }} disabled={!enabled || busy}>Add domain</button>
      </form>

      {error && <p className="text-danger small m-0" role="alert">{error}</p>}
      {error && <ErrorReference error={failure} className="mt-0" />}

      {domains.length === 0
        ? <p className="sw-hint m-0">No domains yet. Add the email domain people here sign in with.</p>
        : <ul className="sw-rows">
          {domains.map(domain =>
            <OrgSsoDomainRow key={domain.id} domain={domain} connections={connections} enabled={enabled} onChanged={handleRefresh} onBindingsChanged={handleBindingsChanged} />)}
        </ul>}
    </div>
  )
}

const OrgSsoDomainRow = ({ domain, connections, enabled, onChanged, onBindingsChanged = onChanged }) => {
  const orgSsoDomain = useApiFormOrgSsoDomain()

  const [selectedProviderID, setSelectedProviderID] = useState('')
  const [notifySent, setNotifySent] = useState(null)
  const [removing, setRemoving] = useState(false)
  const [removeBusy, setRemoveBusy] = useState(false)
  const [removeError, removeFailure, setRemoveError] = useErrorLine()
  const [unbinding, setUnbinding] = useState(null)
  const [unbindBusy, setUnbindBusy] = useState(false)
  const [unbindError, unbindFailure, setUnbindError] = useErrorLine()

  const tone = domainTone(domain)

  // Every connection linked to the organization, owned or not (audit B6).
  // Only owned connections can be bound or unbound from here -- the server
  // refuses the rest -- so those are shown read-only with who to ask.
  const boundProviderIDs = domain.providerIDs || []
  const boundConnections = connections.filter(c => boundProviderIDs.includes(c.id))
  const bindableConnections = connections.filter(c => c.owned && !boundProviderIDs.includes(c.id))
  const managedOnly = connections.length > 0 && !connections.some(c => c.owned)
  const managedBound = boundConnections.filter(c => !c.owned)

  // Check now: "Checking…" held a moment, then what DNS showed, in words
  // (domain-check.js), and the per-domain cooldown's countdown.
  const checkNow = useDomainCheck({
    domain: domain.domain,
    run: () => orgSsoDomain.postArg(`/${domain.id}/check`),
    onChecked: () => onChanged && onChanged(),
    failure: `${domain.domain} couldn't be checked right now.`
  })

  const handleRequiredChange = async e => {
    await orgSsoDomain.put(`${domain.id}/sso-required`, { required: e.target.checked })
    onChanged && (await onChanged())
  }

  const handleBind = async () => {
    if (!selectedProviderID) return

    const match = bindableConnections.find(c => String(c.id) === selectedProviderID)
    await orgSsoDomain.postArg(`/${domain.id}/bindings`, { providerID: match ? match.id : selectedProviderID })
    setSelectedProviderID('')
    onBindingsChanged && (await onBindingsChanged())
  }

  const handleUnbind = async deactivate => {
    setUnbindBusy(true)
    setUnbindError(null)
    try {
      await orgSsoDomain.delete(`${domain.id}/bindings/${unbinding.id}${deactivate ? '?deactivate=true' : ''}`)
    } catch (err) {
      setUnbindError(serverMessage(err, `Couldn't unbind ${domain.domain}.`), err)
      setUnbindBusy(false)
      return
    }
    setUnbindBusy(false)
    setUnbinding(null)
    onBindingsChanged && (await onBindingsChanged())
  }

  const handleRemove = async () => {
    setRemoveBusy(true)
    setRemoveError(null)
    try {
      await orgSsoDomain.delete(domain.id)
    } catch (err) {
      setRemoveError(serverMessage(err, `Couldn't remove ${domain.domain}.`), err)
      setRemoveBusy(false)
      return
    }
    setRemoveBusy(false)
    setRemoving(false)
    onBindingsChanged && (await onBindingsChanged())
  }

  const handleNotify = async () => {
    const result = await orgSsoDomain.postArg(`/${domain.id}/notify-members`)
    setNotifySent((result && result.sent) || 0)
  }

  const requiredID = `org-sso-domain-required-${domain.id}`

  return (
    <li className="sw-domain d-grid gap-2" data-testid={`domain-${domain.domain}`}>
      <div className="d-flex flex-wrap align-items-center gap-2">
        <code className="fw-semibold">{domain.domain}</code>
        {/* An active override's detail is on the page beside the pill, so the
            pill doesn't repeat it as hover text. */}
        <DomainPill tone={tone} title={tone === 'overridden' ? undefined : overrideDetail(domain) || undefined} />
        {domain.state === 'at_risk' && domain.deadlineAt &&
          <span className="sw-hint">Verify again by {formatDate(domain.deadlineAt)}</span>}
        {tone === 'overridden' && <span className="sw-hint">{overrideDetail(domain)}</span>}
      </div>

      {!domain.lastOkAt &&
        <React.Fragment>
          <div className="sw-panel">
            <p className="sw-hint m-0">Add this TXT record at your DNS host to verify {domain.domain}:</p>
            <TxtRecordFields idPrefix={`org-txt-${domain.id}`} domain={domain.domain} token={domain.txtToken} fullName={domain.txtName} relativeName={domain.txtRelativeName} dnsHost={domain.dnsHost} />
          </div>
          <p className="sw-hint m-0">{TXT_KEEP_HINT}</p>
        </React.Fragment>}

      <DomainCheckLine line={checkNow.line} />
      {checkNow.error && <p className="text-danger small m-0" role="alert">{checkNow.error.text}</p>}
      {checkNow.error && <ErrorReference error={checkNow.error.failure} className="mt-0" />}

      <div className="d-flex flex-wrap align-items-center gap-3">
        <button type="button" className="btn btn-sm sw-btn" disabled={!enabled || checkNow.disabled} onClick={checkNow.check}>{checkNow.label}</button>

        {domain.state === 'deactivated' &&
          <button type="button" className="btn btn-sm sw-btn" disabled={!enabled} onClick={handleNotify}>Notify members</button>}

        <button type="button" className="btn btn-sm sw-btn-outline-danger" disabled={!enabled} onClick={() => { setRemoveError(null); setRemoving(true) }}>
          Remove<span className="visually-hidden"> {domain.domain}</span>
        </button>
      </div>

      {notifySent !== null &&
        <p className="sw-hint m-0" role="status">Notified {notifySent} member{notifySent === 1 ? '' : 's'}.</p>}

      {/* On its own line so its explanation sits right under it. */}
      <RequireSSOSwitch id={requiredID} domain={domain.domain} checked={domain.ssoRequired} disabled={!enabled} onChange={handleRequiredChange} />

      <div className="d-flex flex-wrap align-items-center gap-2">
        <span className="sw-hint">Sends people to:</span>
        {boundConnections.length === 0 && <span className="sw-hint">no connection yet</span>}
        {boundConnections.map(connection =>
          <span key={connection.id} className="sw-tag">
            {connectionName(connection)}
            {Array.isArray(connection.testAllowlist) && connection.testAllowlist.length > 0 &&
              <Link className="sw-tag-badge" to={`/org/sso/connections/${connection.id}#testing`}
                title="Only its test addresses are sent to it. Open its Testing section.">
                Testing · {connection.testAllowlist.length === 1 ? '1 person' : `${connection.testAllowlist.length} people`}
              </Link>}
            {connection.owned
              ? <button type="button" className="sw-tag-x" disabled={!enabled}
                aria-label={`Unbind ${domain.domain} from ${connectionName(connection)}`}
                onClick={() => { setUnbindError(null); setUnbinding(connection) }}>×</button>
              : <span className="pe-2" />}
          </span>)}
      </div>

      {/* Only about the connections EducationXR manages: the organization's
          own ones it binds and unbinds right here. */}
      {managedBound.length > 0 &&
        <p className="sw-hint m-0">Connections EducationXR manages ({managedBound.map(connectionName).join(', ')}): contact support to change them.</p>}

      {bindableConnections.length > 0 &&
        <div className="d-flex flex-wrap align-items-center gap-2">
          <select className="form-select form-select-sm w-auto" aria-label={`Bind ${domain.domain} to a connection`} disabled={!enabled}
            value={selectedProviderID} onChange={e => setSelectedProviderID(e.target.value)}>
            <option value="">Choose a connection</option>
            {bindableConnections.map(connection =>
              <option key={connection.id} value={connection.id}>{connectionName(connection)}</option>)}
          </select>
          <button type="button" className="btn btn-sm sw-btn" disabled={!enabled || !selectedProviderID} onClick={handleBind}>Bind</button>
        </div>}

      {managedOnly && boundConnections.length === 0 &&
        <p className="sw-hint m-0">Your connections are managed by EducationXR. Contact support to bind {domain.domain} to one.</p>}

      <UnbindDialog
        open={Boolean(unbinding)}
        domain={domain.domain}
        connectionName={unbinding ? connectionName(unbinding) : ''}
        // The server's count across every organization, never this page's
        // own domains: a shared connection's last domain HERE is not its
        // last one (review I4). No count, no deactivate offer.
        lastOfActive={Boolean(unbinding && unbinding.active && !unbinding.public && typeof unbinding.boundDomainCount === 'number' && unbinding.boundDomainCount <= 1)}
        busy={unbindBusy}
        error={unbindError}
        failure={unbindFailure}
        onUnbind={() => handleUnbind(false)}
        onUnbindAndDeactivate={() => handleUnbind(true)}
        onCancel={() => setUnbinding(null)}
      />

      <ConfirmDialog
        open={removing}
        title={`Remove ${domain.domain}?`}
        body={`People at ${domain.domain} stop being sent to single sign-on, and the domain can be claimed again.`}
        confirmText="Remove domain"
        busy={removeBusy}
        error={removeError}
        failure={removeFailure}
        onConfirm={handleRemove}
        onCancel={() => setRemoving(false)}
      />
    </li>
  )
}
